import { useEffect } from 'react';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogType?: string;
  schema?: object[];
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonical,
  ogType = 'website',
  schema = []
}: SEOHeadProps) {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useEffect(() => {
    // Update title
    if (title) {
      document.title = title;
    }

    // Update meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    if (description) {
      updateMetaTag('description', description);
      updateMetaTag('og:description', description, true);
      updateMetaTag('twitter:description', description);
    }

    if (keywords) {
      updateMetaTag('keywords', keywords);
    }

    if (title) {
      updateMetaTag('og:title', title, true);
      updateMetaTag('twitter:title', title);
    }

    if (ogType) {
      updateMetaTag('og:type', ogType, true);
    }

    // Update canonical
    if (canonical) {
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', `${siteUrl}${canonical}`);
      updateMetaTag('og:url', `${siteUrl}${canonical}`, true);
    }

    // Update last-modified
    const today = new Date().toISOString().split('T')[0];
    updateMetaTag('last-modified', today);

    // Add schema.org JSON-LD
    if (schema.length > 0) {
      // Remove existing schema scripts added by this component
      const existingSchemas = document.querySelectorAll('script[data-schema="dynamic"]');
      existingSchemas.forEach(s => s.remove());

      schema.forEach((schemaObj) => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-schema', 'dynamic');
        
        // Replace placeholder with actual site URL
        const schemaString = JSON.stringify(schemaObj, null, 2);
        const updatedSchema = schemaString.replace(/SITE_URL_PLACEHOLDER/g, siteUrl);
        
        script.textContent = updatedSchema;
        document.head.appendChild(script);
      });
    }
  }, [title, description, keywords, canonical, ogType, schema, siteUrl]);

  return null;
}
